import { ASPECT_RATIO, MAX_IMAGE_SIZE_FACTOR, MIN_IMAGE_FRAME_PERCENTAGE } from '../../../CreatePageConstants';
import { IMAGE_ELEMENT_CONFIG } from '../../../Preview/PreviewDisplayConstants';

const FRAME_WIDTH_TO_HEIGHT = {
    [ASPECT_RATIO.LANDSCAPE]: 16 / 9,
    [ASPECT_RATIO.PORTRAIT]: 9 / 16,
    [ASPECT_RATIO.SQUARE]: 1
};

// The frame in the units the image element settings are written in: a frame
// IMAGE_ELEMENT_CONFIG.BASE_HEIGHT units tall, which the renderer scales up to the export height.
export function getFrameBaseSize(aspectRatio) {
    const widthToHeight = FRAME_WIDTH_TO_HEIGHT[aspectRatio] ?? FRAME_WIDTH_TO_HEIGHT[ASPECT_RATIO.LANDSCAPE];
    const baseHeight = IMAGE_ELEMENT_CONFIG.BASE_HEIGHT;

    return { width: baseHeight * widthToHeight, height: baseHeight };
}

// How far from the middle of the frame a picture's position is allowed to travel, on each axis. The
// frame is what decides this, not the sliders: a limit taken from the widest shape lets a picture in
// a portrait frame be dragged more than 300 units past the edge of a canvas only 281 units wide.
//
// Half the frame, so a picture can be carried until its middle reaches the edge and no further, which
// is also what keeps the safe zone lines within reach of a drag. Whole units, because a position is a
// whole slider step; landscape rounds to the 444 the API served before these limits were derived here.
export function getMaxPositionForAspectRatio(aspectRatio) {
    const frameSize = getFrameBaseSize(aspectRatio);

    return {
        maxXPosition: Math.round(frameSize.width / 2),
        maxYPosition: Math.round(frameSize.height / 2)
    };
}

// The size a picture is drawn at before the size factor is applied. Every picture is first fitted
// inside the same square box, so a portrait photo and a wide banner start out equally prominent.
// This is the very calculation ImageElement.fitIntoBox does, kept in base units.
export function calculateFittedSize(naturalSize) {
    const fittedScale = Math.min(
        IMAGE_ELEMENT_CONFIG.BOX_SIZE / naturalSize.width,
        IMAGE_ELEMENT_CONFIG.BOX_SIZE / naturalSize.height
    );

    return { width: naturalSize.width * fittedScale, height: naturalSize.height * fittedScale };
}

// How much of the frame the picture covers on each axis, as a percentage. The two are tied together
// by the picture's own proportions: the size factor scales both at once, so moving one control moves
// the other with it.
export function calculateFramePercentages(sizeFactor, naturalSize, aspectRatio) {
    const fittedSize = calculateFittedSize(naturalSize);
    const frameSize = getFrameBaseSize(aspectRatio);

    return {
        width: (fittedSize.width * sizeFactor) / frameSize.width * 100,
        height: (fittedSize.height * sizeFactor) / frameSize.height * 100
    };
}

// The size factor that makes the picture cover the given share of the frame on the given axis.
export function calculateSizeFactorForPercentage(percentage, axis, naturalSize, aspectRatio) {
    const fittedSize = calculateFittedSize(naturalSize);
    const frameSize = getFrameBaseSize(aspectRatio);

    if (!fittedSize[axis])
        return 0;

    return (percentage / 100) * frameSize[axis] / fittedSize[axis];
}

// The largest the picture is allowed to be. It stops at the first axis that fills the frame, so the
// controls can be read as a share of the frame without either of them ever passing 100.
export function calculateMaximumSizeFactor(naturalSize, aspectRatio) {
    return Math.min(
        calculateSizeFactorForPercentage(100, 'width', naturalSize, aspectRatio),
        calculateSizeFactorForPercentage(100, 'height', naturalSize, aspectRatio),
        MAX_IMAGE_SIZE_FACTOR
    );
}

// The smallest the picture is allowed to be, matching the floor the Width and Height sliders stop at.
// A corner dragged past the middle of the picture asks for a negative size, and this is what it lands
// on instead.
export function calculateMinimumSizeFactor(naturalSize, aspectRatio) {
    return Math.min(
        calculateSizeFactorForPercentage(MIN_IMAGE_FRAME_PERCENTAGE, 'width', naturalSize, aspectRatio),
        calculateSizeFactorForPercentage(MIN_IMAGE_FRAME_PERCENTAGE, 'height', naturalSize, aspectRatio)
    );
}
